/**
 * bitSelectionService — pure JS port of the BitSelectionGuide rule engine.
 * Reads bit-selection-guide.json and resolves a recommendation for the given
 * productLine + rockConditions (+ optional application / rotary inputs).
 *
 * Used by the wizard Step 5/Step 9 results page (D8) and by the live-counts
 * footer in D6 where wizard state may be partial.
 */

import { BIT_SELECTION_GUIDE } from '@data';

const LOW_CONF_EMPTY = Object.freeze({
  recommendation: null,
  alternatives: [],
  confidence: 'low',
  rationale: '',
  sourceCitations: [],
});

/**
 * Evaluate a single rule's `when` predicate against the input.
 * AND-combined across keys. Lookup order: input[key], then input.rockConditions[key].
 * Predicate semantics:
 *  - 'any'                  → always matches
 *  - userIndicatedUnknown   → coerce actual to bool, compare to expected bool
 *  - 2-elem numeric array on key containing 'Range' or 'ucs' → numeric [min, max] check
 *  - array (non-numeric)    → actual must be one of the values
 *  - scalar                 → strict equality
 * Undefined actuals against a non-'any' expected → no match (strict).
 */
function evaluatePredicate(when, input) {
  if (!when || typeof when !== 'object') return true;
  const rc = input.rockConditions || {};

  for (const [key, expected] of Object.entries(when)) {
    if (expected === 'any') continue;

    if (key === 'userIndicatedUnknown') {
      const actual = Boolean(rc.userIndicatedUnknown);
      if (actual !== Boolean(expected)) return false;
      continue;
    }

    // Numeric range: predicate key contains 'Range' or starts with 'ucs',
    // expected is a 2-elem array of numbers. Strip 'Range_' for lookup
    // (e.g. ucsRange_psi → ucsPsi).
    const isRangeKey = /Range/i.test(key) || /^ucs/i.test(key);
    const isNumericRange =
      isRangeKey &&
      Array.isArray(expected) &&
      expected.length === 2 &&
      expected.every((v) => typeof v === 'number');

    if (isNumericRange) {
      // Predicate keys use 'Range' suffix (e.g. ucsRange_psi), but input
      // carries the scalar (e.g. ucsPsi). Map ucsRange_psi → ucsPsi by
      // dropping 'Range' and camelCasing the next segment.
      const lookupKey = key
        .replace(/Range_([a-z])/i, (_, c) => c.toUpperCase())
        .replace(/Range/i, '');
      const rawActual = input[lookupKey] ?? input[key] ?? rc[lookupKey] ?? rc[key];
      if (rawActual === undefined || rawActual === null) return false;
      const num = Number(rawActual);
      if (Number.isNaN(num)) return false;
      const [min, max] = expected;
      if (num < min || num > max) return false;
      continue;
    }

    const actual = input[key] ?? rc[key];

    if (Array.isArray(expected)) {
      if (actual === undefined) return false;
      if (!expected.includes(actual)) return false;
      continue;
    }

    if (actual === undefined) return false;
    if (actual !== expected) return false;
  }

  return true;
}

/**
 * Resolve a bit-grade/design recommendation for the given wizard inputs.
 *
 * @param {Object} input
 * @param {'th'|'dth'|'rotary'} input.productLine
 * @param {Object} [input.rockConditions] - { rockHardness, abrasivity, competence, userIndicatedUnknown }
 * @param {string} [input.application]    - wizard step 1 selection
 * @param {string} [input.bitFamily]      - rotary only: RR240 | RR340 | RR440/RR450
 * @param {number} [input.ucsPsi]         - rotary only
 * @param {string} [input.formation]      - rotary only
 * @returns {{recommendation: object|null, alternatives: Array, confidence: string, rationale: string, sourceCitations: Array}}
 */
export function selectGrade(input = {}) {
  const { productLine } = input;
  const rules = BIT_SELECTION_GUIDE?.selectionRules?.[productLine];

  if (!Array.isArray(rules)) {
    return { ...LOW_CONF_EMPTY, rationale: 'Unknown product line' };
  }

  const matches = rules
    .filter((rule) => evaluatePredicate(rule.when, input))
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));

  if (matches.length === 0) return LOW_CONF_EMPTY;

  const primary = matches[0];
  return {
    recommendation: primary.recommend,
    alternatives: primary.alternatives || [],
    confidence: primary.confidence,
    rationale: primary.rationale || '',
    sourceCitations: [primary.source].filter(Boolean),
  };
}

/** Look up a carbide-grade descriptor by key (XT48, DP55, SH70, ...). */
export function getCarbideGrade(gradeKey) {
  return BIT_SELECTION_GUIDE.carbideGrades[gradeKey];
}

/** Look up a bit-design descriptor (Standard, Retrac, Speedy, Flat, Convex, ...). */
export function getBitDesign(productLine, designKey) {
  const set = productLine === 'dth' ? BIT_SELECTION_GUIDE.bitDesignsDTH : BIT_SELECTION_GUIDE.bitDesignsTH;
  return set[designKey];
}
